/**
 * Insights da Cadeia de Ajuda — interpretação frontend dos dados de aderência.
 * Não altera cálculos de aderência nem regras de escalonamento.
 */

import {
  ANDON_ESCALATION_LEVELS,
  resolveAndonLevelForCargo,
} from './andonEscalation';
import { resolveLeaderGravidade, weekdayLabelFromIso, type CadeiaGravidade } from './cadeiaSeverityStyles';
import type { LstCargo, LstLideradoAderencia } from '../types';

export type { CadeiaGravidade };

export interface CadeiaLeaderScore {
  row: LstLideradoAderencia;
  perdidas: number;
  desvios: number;
  redDays: number;
  aderenciaPct: number;
  criticalLosses: number;
  pendingDesvios: number;
  rotinasNaoRealizadas: number;
  consecutiveLossDays: number;
  severity: number;
  gravidade: CadeiaGravidade;
  piorDiaLabel: string | null;
  chipReason: string | null;
}

export type CadeiaAndonResponsibility = 'agir_agora' | 'proximo_nivel' | 'monitorando' | 'escalonado';

export interface CadeiaAndonLevelStat {
  level: 1 | 2 | 3;
  shortLabel: string;
  label: string;
  toleranceMinutes: number;
  responsibility: CadeiaAndonResponsibility;
  responsibilityLabel: string;
  statusDetail: string;
}

export interface CadeiaRecommendedAction {
  gravidade: CadeiaGravidade;
  whatToDo: string;
  responsible: string;
  reason: string;
  suggestedDeadline: string;
  leaderName: string;
  leaderId: string;
  date: string | null;
}

export interface CadeiaExecutiveImpact {
  gravidade: CadeiaGravidade;
  leaderName: string;
  aderenciaPct: number;
  rotinasNaoRealizadas: number;
  piorDiaLabel: string | null;
  /** Modo operacional — oculta métricas zeradas que não explicam a criticidade. */
  operationalMode: boolean;
  desvios: number;
  perdidas: number;
}

export interface CadeiaChainInsights {
  criticalLeaders: CadeiaLeaderScore[];
  andonLevels: CadeiaAndonLevelStat[];
  recommended: CadeiaRecommendedAction;
  impact: CadeiaExecutiveImpact | null;
  chainHasIssue: boolean;
}

function countRotinasNaoRealizadas(row: LstLideradoAderencia): number {
  return row.ocorrencias.filter((o) => o.status === 'nao_realizado').length;
}

function countConsecutiveLossDays(row: LstLideradoAderencia): number {
  const sorted = [...row.dias].sort((a, b) => b.date.localeCompare(a.date));
  let count = 0;
  for (const d of sorted) {
    if (d.nivel === 'vermelho' || d.perdidas > 0) count++;
    else if (d.nivel !== 'neutro') break;
  }
  return count;
}

function findPiorDiaLabel(row: LstLideradoAderencia): string | null {
  const redDay = row.dias.find((d) => d.nivel === 'vermelho');
  if (redDay) return `${weekdayLabelFromIso(redDay.date)} crítica`;

  let worstDate: string | null = null;
  let worstScore = 0;
  for (const d of row.dias) {
    const s = d.perdidas * 10 + d.desvios;
    if (s > worstScore) {
      worstScore = s;
      worstDate = d.date;
    }
  }
  if (!worstDate || worstScore === 0) {
    const lowAdherence = row.dias.filter((d) => d.nivel === 'laranja' || d.nivel === 'neutro');
    if (lowAdherence.length > 0 && row.dias.every((d) => d.nivel !== 'verde')) {
      const first = row.dias.find((d) => d.nivel !== 'neutro');
      return first ? `${weekdayLabelFromIso(first.date)} em atenção` : null;
    }
    return null;
  }
  return `${weekdayLabelFromIso(worstDate)} crítica`;
}

function findWorstDate(row: LstLideradoAderencia): string | null {
  const redDay = row.dias.find((d) => d.nivel === 'vermelho');
  if (redDay) return redDay.date;

  let worstDate: string | null = null;
  let worstScore = 0;
  for (const d of row.dias) {
    const s = d.perdidas * 10 + d.desvios;
    if (s > worstScore) {
      worstScore = s;
      worstDate = d.date;
    }
  }
  return worstDate;
}

function buildChipReason(score: Omit<CadeiaLeaderScore, 'chipReason' | 'piorDiaLabel'> & { piorDiaLabel: string | null }): string | null {
  if (score.rotinasNaoRealizadas > 0) {
    return `${score.rotinasNaoRealizadas} rotina${score.rotinasNaoRealizadas > 1 ? 's' : ''} não realizada${score.rotinasNaoRealizadas > 1 ? 's' : ''}`;
  }
  if (score.aderenciaPct < 80) return `${score.aderenciaPct}% aderência`;
  if (score.desvios > 0) return `${score.desvios} desvio${score.desvios > 1 ? 's' : ''}`;
  if (score.piorDiaLabel) return score.piorDiaLabel;
  if (score.perdidas > 0) return `${score.perdidas} perda${score.perdidas > 1 ? 's' : ''}`;
  if (score.gravidade !== 'normal') return 'Desempenho abaixo do esperado';
  return null;
}

function scoreLeader(row: LstLideradoAderencia): CadeiaLeaderScore {
  const perdidas = row.dias.reduce((s, d) => s + d.perdidas, 0);
  const desvios = row.dias.reduce((s, d) => s + d.desvios, 0);
  const redDays = row.dias.filter((d) => d.nivel === 'vermelho').length;
  const okDays = row.dias.filter((d) => d.nivel === 'verde').length;
  const aderenciaPct = row.dias.length > 0 ? Math.round((okDays / row.dias.length) * 100) : 100;
  const criticalLosses = row.ocorrencias.filter(
    (o) => o.isCritical && o.status === 'nao_realizado',
  ).length;
  const pendingDesvios = row.ocorrencias.filter((o) => o.status === 'realizado_com_desvio').length;
  const rotinasNaoRealizadas = countRotinasNaoRealizadas(row);
  const consecutiveLossDays = countConsecutiveLossDays(row);
  const piorDiaLabel = findPiorDiaLabel(row);
  const severity =
    criticalLosses * 100 +
    rotinasNaoRealizadas * 15 +
    perdidas * 10 +
    desvios +
    redDays * 5 +
    (aderenciaPct < 60 ? 20 : 0);

  const base = {
    row,
    perdidas,
    desvios,
    redDays,
    aderenciaPct,
    criticalLosses,
    pendingDesvios,
    rotinasNaoRealizadas,
    consecutiveLossDays,
    severity,
    piorDiaLabel,
  };

  const gravidade = resolveLeaderGravidade(base);
  return { ...base, gravidade, chipReason: buildChipReason({ ...base, gravidade }) };
}

const RESPONSIBILITY_LABEL: Record<CadeiaAndonResponsibility, string> = {
  agir_agora: 'Agir agora',
  proximo_nivel: 'Próximo nível',
  monitorando: 'Monitorando',
  escalonado: 'Escalonado',
};

function computeAndonResponsibility(
  lvl: 1 | 2 | 3,
  viewerLevel: 1 | 2 | 3,
  chainHasIssue: boolean,
  hasRed: boolean,
): Pick<CadeiaAndonLevelStat, 'responsibility' | 'responsibilityLabel' | 'statusDetail'> {
  if (!chainHasIssue) {
    return {
      responsibility: 'monitorando',
      responsibilityLabel: RESPONSIBILITY_LABEL.monitorando,
      statusDetail: 'Sem pendências na cadeia',
    };
  }

  const slaBreach = hasRed;

  if (slaBreach) {
    if (lvl === viewerLevel) {
      if (viewerLevel >= 3) {
        return {
          responsibility: 'agir_agora',
          responsibilityLabel: RESPONSIBILITY_LABEL.agir_agora,
          statusDetail: 'Responsável executivo — SLA estourado',
        };
      }
      return {
        responsibility: 'escalonado',
        responsibilityLabel: RESPONSIBILITY_LABEL.escalonado,
        statusDetail: 'Caso escalonado — aguardando nível superior',
      };
    }
    if (lvl === viewerLevel + 1) {
      return {
        responsibility: 'agir_agora',
        responsibilityLabel: RESPONSIBILITY_LABEL.agir_agora,
        statusDetail: 'Responsável atual — SLA estourado',
      };
    }
    if (lvl < viewerLevel) {
      return {
        responsibility: 'escalonado',
        responsibilityLabel: RESPONSIBILITY_LABEL.escalonado,
        statusDetail: 'Escalonado do nível inferior',
      };
    }
    return {
      responsibility: 'monitorando',
      responsibilityLabel: RESPONSIBILITY_LABEL.monitorando,
      statusDetail: 'Sem ação imediata',
    };
  }

  if (lvl === viewerLevel) {
    return {
      responsibility: 'agir_agora',
      responsibilityLabel: RESPONSIBILITY_LABEL.agir_agora,
      statusDetail: 'Responsável atual',
    };
  }
  if (lvl === viewerLevel + 1) {
    return {
      responsibility: 'proximo_nivel',
      responsibilityLabel: RESPONSIBILITY_LABEL.proximo_nivel,
      statusDetail: 'Escalonamento se não tratado',
    };
  }
  if (lvl < viewerLevel) {
    return {
      responsibility: 'monitorando',
      responsibilityLabel: RESPONSIBILITY_LABEL.monitorando,
      statusDetail: 'Origem monitorada',
    };
  }
  return {
    responsibility: 'monitorando',
    responsibilityLabel: RESPONSIBILITY_LABEL.monitorando,
    statusDetail: 'Sem ação imediata',
  };
}

function computeAndonLevels(
  rows: LstLideradoAderencia[],
  viewerLevel: 1 | 2 | 3,
  chainHasIssue: boolean,
): CadeiaAndonLevelStat[] {
  const hasRed = rows.some((r) => r.dias.some((d) => d.nivel === 'vermelho'));

  return ANDON_ESCALATION_LEVELS.map((lvl) => {
    const resp = computeAndonResponsibility(lvl.level, viewerLevel, chainHasIssue, hasRed);
    return {
      level: lvl.level,
      shortLabel: lvl.shortLabel,
      label: lvl.label,
      toleranceMinutes: lvl.toleranceMinutes,
      ...resp,
    };
  });
}

function buildReason(worst: CadeiaLeaderScore): string {
  const parts: string[] = [];
  if (worst.rotinasNaoRealizadas > 0) {
    parts.push(`${worst.rotinasNaoRealizadas} rotina(s) não realizada(s)`);
  }
  if (worst.aderenciaPct < 80 && parts.length < 2) {
    parts.push(`${worst.aderenciaPct}% aderência`);
  }
  if (worst.desvios > 0 && parts.length < 2) {
    parts.push(`${worst.desvios} desvio(s) sem tratamento`);
  }
  if (worst.perdidas > 0 && parts.length < 2) {
    parts.push(`${worst.perdidas} perda(s) crítica(s) na semana`);
  }
  if (worst.piorDiaLabel && parts.length < 2) {
    parts.push(worst.piorDiaLabel);
  }
  return parts.slice(0, 2).join(' · ') || 'Desempenho abaixo do esperado na semana';
}

function computeRecommended(
  leaders: CadeiaLeaderScore[],
  viewerLevel: 1 | 2 | 3,
): CadeiaRecommendedAction {
  const worst = leaders[0];
  const viewerMeta = ANDON_ESCALATION_LEVELS.find((l) => l.level === viewerLevel)!;

  if (!worst || worst.gravidade === 'normal') {
    return {
      gravidade: 'normal',
      whatToDo: 'Manter rotina de gemba e acompanhamento semanal',
      responsible: viewerMeta.shortLabel,
      reason: 'Sem perdas ou desvios relevantes na semana',
      suggestedDeadline: 'Próximo ciclo de liderança',
      leaderName: '',
      leaderId: '',
      date: null,
    };
  }

  const nextLvl = ANDON_ESCALATION_LEVELS.find((l) => l.level === Math.min(viewerLevel + 1, 3));
  const deadlineMeta = viewerLevel >= 3 ? viewerMeta : nextLvl ?? viewerMeta;

  let whatToDo: string;
  let responsible: string;

  if (worst.gravidade === 'critico') {
    if (viewerLevel >= 3) {
      whatToDo = `Convocar ${worst.row.member.name} para gemba imediato e plano corretivo`;
      responsible = viewerMeta.shortLabel;
    } else {
      whatToDo = `Escalonar caso de ${worst.row.member.name} para ${nextLvl?.shortLabel ?? 'nível superior'}`;
      responsible = nextLvl?.shortLabel ?? 'Nível superior';
    }
  } else if (worst.rotinasNaoRealizadas > 0) {
    whatToDo = `Tratar ${worst.rotinasNaoRealizadas} rotina(s) não realizada(s) de ${worst.row.member.name}`;
    responsible = viewerMeta.shortLabel;
  } else {
    whatToDo = `Tratar desvios de ${worst.row.member.name} antes do escalonamento Andon`;
    responsible = viewerMeta.shortLabel;
  }

  return {
    gravidade: worst.gravidade,
    whatToDo,
    responsible,
    reason: buildReason(worst),
    suggestedDeadline: `${deadlineMeta.toleranceMinutes} min (SLA Andon N${deadlineMeta.level})`,
    leaderName: worst.row.member.name,
    leaderId: worst.row.member.id,
    date: findWorstDate(worst.row),
  };
}

function needsOperationalImpact(score: CadeiaLeaderScore): boolean {
  return score.aderenciaPct < 80 || score.rotinasNaoRealizadas > 0 || score.redDays > 0;
}

function computeImpact(worst: CadeiaLeaderScore | undefined): CadeiaExecutiveImpact | null {
  if (!worst || worst.gravidade === 'normal') return null;

  const operationalMode = needsOperationalImpact(worst);

  return {
    gravidade: worst.gravidade,
    leaderName: worst.row.member.name,
    aderenciaPct: worst.aderenciaPct,
    rotinasNaoRealizadas: worst.rotinasNaoRealizadas,
    piorDiaLabel: worst.piorDiaLabel,
    operationalMode,
    desvios: worst.desvios,
    perdidas: worst.perdidas,
  };
}

export function computeCadeiaChainInsights(
  rows: LstLideradoAderencia[],
  viewerCargo: LstCargo,
): CadeiaChainInsights {
  const viewerLevel = resolveAndonLevelForCargo(viewerCargo);
  const criticalLeaders = rows.map(scoreLeader).sort((a, b) => b.severity - a.severity);
  const worst = criticalLeaders[0];
  const chainHasIssue = (worst?.gravidade ?? 'normal') !== 'normal';

  return {
    criticalLeaders,
    andonLevels: computeAndonLevels(rows, viewerLevel, chainHasIssue),
    recommended: computeRecommended(criticalLeaders, viewerLevel),
    impact: computeImpact(worst),
    chainHasIssue,
  };
}

export interface DrawerPrescriptiveAction {
  whatToDo: string;
  responsible: string;
  reason: string;
  suggestedDeadline: string;
  gravidade: CadeiaGravidade;
}

/** Ação prescriptiva para o drawer de diagnóstico. */
export function buildDrawerPrescriptiveAction(
  paretoTopLabel: string | null,
  hasCorrectiveActions: boolean,
  hasCriticalLoss: boolean,
  naoRealizado: number,
  desvio: number,
): DrawerPrescriptiveAction {
  const gravidade =
    naoRealizado > 0 || hasCriticalLoss ? 'critico' : desvio > 0 ? 'atencao' : 'normal';

  if (hasCorrectiveActions) {
    return {
      gravidade: gravidade === 'normal' ? 'atencao' : gravidade,
      whatToDo: 'Validar encerramento do plano corretivo aberto no gemba',
      responsible: 'Responsável da ação',
      reason: 'Plano corretivo já registrado — acompanhar execução',
      suggestedDeadline: '48 h',
    };
  }

  if (hasCriticalLoss) {
    return {
      gravidade: 'critico',
      whatToDo: 'Convocar reunião de camada e registrar plano corretivo imediato',
      responsible: 'Supervisor da área',
      reason: 'Rotina crítica não realizada no dia',
      suggestedDeadline: '30 min (SLA Andon N1)',
    };
  }

  if (naoRealizado > 0) {
    return {
      gravidade: 'critico',
      whatToDo: 'Registrar justificativa e plano de ação no gemba',
      responsible: 'Líder da camada',
      reason: `${naoRealizado} rotina${naoRealizado > 1 ? 's' : ''} não realizada${naoRealizado > 1 ? 's' : ''}`,
      suggestedDeadline: '24 h',
    };
  }

  if (paretoTopLabel) {
    return {
      gravidade,
      whatToDo: `Tratar causa recorrente: ${paretoTopLabel}`,
      responsible: 'Líder da camada',
      reason: `Causa dominante identificada (${paretoTopLabel})`,
      suggestedDeadline: desvio > 0 ? '24 h' : 'Próximo ciclo',
    };
  }

  return {
    gravidade,
    whatToDo: 'Registrar justificativa e plano de ação no gemba',
    responsible: 'Líder da camada',
    reason:
      desvio > 0
        ? `${desvio} desvio(s) sem causa raiz registrada`
        : 'Desvio registrado sem plano corretivo',
    suggestedDeadline: '24 h',
  };
}
